import axios from 'axios';

/**
 * Centralized API client for the CHIV backend.
 *
 * - Base URL: `${REACT_APP_BACKEND_URL}/api`
 * - Auth: in-memory access token (set via setAccessToken); cookie carries refresh
 * - Response envelope unwrap: backend returns { success, data } — we expose `.data`
 * - Auto-refresh on 401: tries POST /auth/refresh once, retries the original request
 *
 * Components should import this default `api` and call api.get('/inquiries') etc.
 * They should NOT read process.env.REACT_APP_BACKEND_URL anywhere else.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
export const API_BASE = `${BACKEND_URL.replace(/\/$/, '')}/api`;

let accessToken = null;
let refreshPromise = null;

export function setAccessToken(token) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // for httpOnly refresh cookie
  timeout: 30_000,
});

// Attach Authorization header on every request when we have a token
api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Unwrap success envelope and handle refresh on 401
api.interceptors.response.use(
  (response) => {
    const body = response.data;
    if (body && typeof body === 'object' && body.success === true && 'data' in body) {
      response.data = body.data;
    }
    return response;
  },
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const errBody = error.response?.data;

    // Normalize error message so toast.error(error.message) shows something useful
    if (errBody && typeof errBody === 'object' && errBody.success === false && errBody.error) {
      error.message = errBody.error.message || error.message;
      error.code = errBody.error.code;
      error.details = errBody.error.details;
    }

    // Auto-refresh once on 401 for non-auth endpoints
    const isAuthRoute = (original.url || '').includes('/auth/');
    if (status === 401 && !isAuthRoute && !original._retried) {
      original._retried = true;
      try {
        refreshPromise = refreshPromise || api.post('/auth/refresh').finally(() => (refreshPromise = null));
        const refreshed = await refreshPromise;
        const newToken = refreshed?.data?.accessToken;
        if (newToken) {
          setAccessToken(newToken);
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${newToken}`;
          return api.request(original);
        }
      } catch {
        // Fall through to reject
      }
    }
    return Promise.reject(error);
  },
);

export default api;
